import React from "react";
import { Box, Container, Grid, Typography, Paper, styled } from "@mui/material";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolIcon from "@mui/icons-material/School";
import { useTranslation } from "react-i18next";
import {
  HeroSection,
  HeroTitle,
  HeroSubtitle,
} from "../../components/hero-section/HeroSection";
import useAboutStats from "../../hooks/api-request/useAboutStats";

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  borderRadius: theme.shape.borderRadius * 1.5,
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "2.5rem",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const About = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const { data: statsData } = useAboutStats();

  const stats = [
    {
      icon: <AutoStoriesIcon />,
      number: `${statsData?.total_books || 0}+`,
      label: t('about.stats.books.label'),
      description: t('about.stats.books.description'),
    },
    {
      icon: <GroupsIcon />,
      number: `${statsData?.total_readers || 0}+`,
      label: t('about.stats.readers.label'),
      description: t('about.stats.readers.description'),
    },
    {
      icon: <TrendingUpIcon />,
      number: "15+",
      label: t('about.stats.experience.label'),
      description: t('about.stats.experience.description'),
    },
    {
      icon: <SchoolIcon />,
      number: `${statsData?.total_authors || 0}+`,
      label: t('about.stats.authors.label'),
      description: t('about.stats.authors.description'),
    },
  ];

  const values = [
    {
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
    },
    {
      title: t('about.values.integrity.title'),
      description: t('about.values.integrity.description'),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <HeroTitle>{t('about.hero.title')}</HeroTitle>
              <HeroSubtitle>
                {t('about.hero.subtitle')}
              </HeroSubtitle>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Stats Section */}
      <ContentSection>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <StatCard elevation={0}>
                  {stat.icon}
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {stat.number}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.description}
                  </Typography>
                </StatCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      {/* Mission Section */}
      <ContentSection sx={{ backgroundColor: "background.paper" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
                {t('about.mission.title')}
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                {t('about.mission.description')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
                {t('about.values.title')}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {values.map((value, index) => (
                  <Box key={index}>
                    <Typography variant="h6" gutterBottom>
                      {value.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {value.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </ContentSection>
    </Box>
  );
};

export default About;
